import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, ChatCircle, Lock, Play, WaveSine, Timer, Images, Flag } from "@phosphor-icons/react";
import AuthMedia from "@/components/AuthMedia";
import Countdown from "@/components/Countdown";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
import { useAudio } from "@/context/AudioContext";
import { useAuth } from "@/context/AuthContext";
import { fileUrl, teaserUrl } from "@/lib/api";
import api from "@/lib/api";
import { toast } from "sonner";

export default function PostCard({ post, index = 0, onCheckout }) {
  const { user } = useAuth();
  const { play, track, playing } = useAudio();
  const [liked, setLiked] = useState(post.liked);
  const [likes, setLikes] = useState(post.likes);
  const [promoLive, setPromoLive] = useState(!!post.promo_active);

  const creator = post.creator || {};
  const avatar = creator.avatar_id ? fileUrl(creator.avatar_id) : null;
  const images = (post.media || []).filter((m) => m.kind === "image");
  const firstMedia = post.media?.[0];
  const isAudioPost = post.post_type === "audio";
  const audioFile = post.media?.find((m) => m.kind === "audio");
  const coverFile = post.media?.find((m) => m.kind === "image");
  const showPromo = post.visibility === "ppv" && promoLive && post.promo_expires_at;
  const unlockPrice = showPromo ? post.promo_price : post.ppv_price;

  const toggleLike = async () => {
    if (!user) return toast("Log in to like");
    try {
      if (liked) {
        const { data } = await api.delete(`/posts/${post.id}/like`);
        setLiked(false); setLikes(data.likes);
      } else {
        const { data } = await api.post(`/posts/${post.id}/like`);
        setLiked(true); setLikes(data.likes);
      }
    } catch { toast.error("Something went wrong"); }
  };

  const report = async () => {
    if (!user) return toast("Log in to report");
    const reason = window.prompt("Why are you reporting this post?");
    if (!reason || !reason.trim()) return;
    try {
      await api.post("/reports", { target_type: "post", target_id: post.id, reason: reason.trim() });
      toast.success("Report submitted — our team will review it.");
    } catch { toast.error("Could not submit report"); }
  };

  const activeAudio = track && audioFile && track.fileId === audioFile.id && playing;

  return (
    <motion.article
      data-testid={`post-card-${post.id}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-30px" }}
      transition={{ duration: 0.55, delay: (index % 5) * 0.04 }}
      className="bg-card border border-border overflow-hidden"
    >
      <div className="flex items-center gap-3 p-4">
        <Link to={`/u/${creator.username}`} className="h-10 w-10 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center overflow-hidden shrink-0">
          {avatar ? <img src={avatar} alt="" className="h-full w-full object-cover" /> :
            <span className="font-accent font-bold text-sm">{(creator.display_name || "?")[0]}</span>}
        </Link>
        <div className="min-w-0">
          <Link to={`/u/${creator.username}`} className="font-accent font-bold text-sm hover:text-primary transition-colors truncate block">
            {creator.display_name}
          </Link>
          <span className="text-[11px] text-muted-foreground uppercase tracking-wider">{post.category}</span>
        </div>
        {post.visibility !== "free" && (
          <span className={`ml-auto px-2 py-1 border text-[10px] font-accent uppercase tracking-wider ${showPromo ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground"}`}>
            {post.visibility === "ppv"
              ? (showPromo ? `$${post.promo_price} offer` : `$${post.ppv_price} PPV`)
              : "Subscribers"}
          </span>
        )}
      </div>

      {post.title && <h3 className="font-display text-2xl px-4 pb-2 leading-tight">{post.title}</h3>}

      {/* Media / lock */}
      {post.locked ? (
        <div className="relative aspect-[4/5] flex flex-col items-center justify-center text-center px-8 border-y border-border overflow-hidden bg-gradient-to-br from-muted to-background">
          {post.teaser_id && (
            <img
              src={teaserUrl(post.teaser_id)}
              alt=""
              aria-hidden
              data-testid={`teaser-${post.id}`}
              className="absolute inset-0 h-full w-full object-cover scale-110 blur-xl opacity-70"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/40" />

          {/* multi-photo indicator */}
          {post.media_count > 1 && (
            <span data-testid={`photo-count-${post.id}`} className="absolute top-3 right-3 z-20 flex items-center gap-1.5 glass border border-white/20 px-2.5 py-1 text-[11px] font-accent uppercase tracking-wider text-white">
              <Images size={14} weight="fill" /> {post.media_count} photos
            </span>
          )}

          {/* limited-time promo countdown */}
          {showPromo && (
            <div data-testid={`promo-badge-${post.id}`} className="absolute top-3 left-3 z-20 flex items-center gap-1.5 bg-primary text-primary-foreground px-2.5 py-1 text-[11px] font-accent font-bold uppercase tracking-wider">
              <Timer size={14} weight="fill" />
              <Countdown to={post.promo_expires_at} testId={`countdown-${post.id}`} onExpire={() => setPromoLive(false)} />
            </div>
          )}

          <div className="relative z-10 flex flex-col items-center">
            <div className="h-14 w-14 rounded-full glass border border-white/20 flex items-center justify-center mb-4">
              <Lock size={26} weight="fill" className="text-primary" />
            </div>
            <p className="font-display text-3xl mb-2 text-white">
              {post.media_count > 1 ? `${post.media_count} locked photos` : "Locked content"}
            </p>
            <p className="text-sm text-white/70 mb-6 max-w-xs">
              {post.visibility === "ppv"
                ? (showPromo
                    ? `Limited-time price — grab this ${isAudioPost ? "audio story" : "set"} before the timer runs out.`
                    : `Unlock this ${isAudioPost ? "audio story" : "post"} for a one-time $${post.ppv_price}.`)
                : `Subscribe to ${creator.display_name} to reveal this.`}
            </p>
            <button
              data-testid={`unlock-btn-${post.id}`}
              onClick={() => onCheckout?.(post)}
              className="px-8 py-3 bg-primary text-primary-foreground font-accent font-bold uppercase tracking-widest text-xs transition-transform active:scale-95 hover:bg-[#e6004c]"
            >
              {post.visibility === "ppv"
                ? (showPromo
                    ? <>Unlock · <span className="line-through opacity-60 mr-1">${post.ppv_price}</span> ${post.promo_price}</>
                    : `Unlock · $${post.ppv_price}`)
                : "Subscribe to unlock"}
            </button>
          </div>
        </div>
      ) : isAudioPost && audioFile ? (
        <div className="relative aspect-[16/10] overflow-hidden border-y border-border">
          {coverFile ? (
            <AuthMedia id={coverFile.id} kind="image" className="absolute inset-0 h-full w-full object-cover" />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-secondary/40 to-primary/30" />
          )}
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <button
              data-testid={`play-audio-${post.id}`}
              onClick={() => play({ fileId: audioFile.id, title: post.title || post.caption || "Audio Story", creator: creator.display_name })}
              className="h-20 w-20 rounded-full bg-primary text-primary-foreground flex items-center justify-center transition-transform active:scale-90 hover:scale-105"
            >
              {activeAudio ? <WaveSine size={30} weight="fill" /> : <Play size={30} weight="fill" />}
            </button>
          </div>
        </div>
      ) : images.length > 1 ? (
        <div className="relative border-y border-border" data-testid={`carousel-${post.id}`}>
          <Carousel opts={{ loop: true }} className="w-full">
            <CarouselContent>
              {images.map((m, idx) => (
                <CarouselItem key={m.id}>
                  <AuthMedia id={m.id} kind="image" className="w-full max-h-[720px] object-cover" testId={`slide-${post.id}-${idx}`} />
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious data-testid={`carousel-prev-${post.id}`} className="left-3 bg-black/50 border-white/20 text-white hover:bg-primary hover:text-primary-foreground" />
            <CarouselNext data-testid={`carousel-next-${post.id}`} className="right-3 bg-black/50 border-white/20 text-white hover:bg-primary hover:text-primary-foreground" />
          </Carousel>
          <span className="absolute top-3 right-3 z-10 flex items-center gap-1.5 glass border border-white/20 px-2.5 py-1 text-[11px] font-accent uppercase tracking-wider text-white pointer-events-none">
            <Images size={14} weight="fill" /> {images.length}
          </span>
        </div>
      ) : firstMedia ? (
        <div className="border-y border-border">
          <AuthMedia
            id={firstMedia.id}
            kind={firstMedia.kind}
            className="w-full max-h-[720px] object-cover"
            testId={`media-${post.id}`}
          />
        </div>
      ) : null}

      <div className="p-4">
        {post.caption && <p className="text-sm text-foreground/90 mb-4 leading-relaxed">{post.caption}</p>}
        <div className="flex items-center gap-5">
          <button data-testid={`like-btn-${post.id}`} onClick={toggleLike} className="flex items-center gap-1.5 group">
            <Heart size={22} weight={liked ? "fill" : "regular"} className={liked ? "text-primary" : "text-muted-foreground group-hover:text-primary transition-colors"} />
            <span className="text-sm text-muted-foreground tabular-nums">{likes}</span>
          </button>
          <Link to={`/post/${post.id}`} data-testid={`comment-btn-${post.id}`} className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors">
            <ChatCircle size={22} />
            <span className="text-sm tabular-nums">{post.comments}</span>
          </Link>
          {post.tags?.length > 0 && (
            <div className="ml-auto flex gap-2 overflow-hidden">
              {post.tags.slice(0, 3).map((t) => (
                <span key={t} className="text-[11px] text-primary/80">#{t}</span>
              ))}
            </div>
          )}
          <button data-testid={`report-btn-${post.id}`} onClick={report}
            className={`${post.tags?.length ? "" : "ml-auto"} text-muted-foreground hover:text-primary transition-colors`}
            title="Report">
            <Flag size={18} />
          </button>
        </div>
      </div>
    </motion.article>
  );
}
